import { useEffect, useRef, useState } from "react";
import { useAccount } from "@/lib/account";
import { useI18n } from "@/lib/i18n";

declare global {
  interface Window {
    google?: { accounts: { id: { initialize: (c: object) => void; renderButton: (el: HTMLElement, o: object) => void } } };
  }
}

/** Official Google button (GIS). Renders nothing until GOOGLE_CLIENT_ID is configured. */
export function GoogleSignIn({ onDone }: { onDone?: () => void }) {
  const { googleClientId, signInWithGoogle } = useAccount();
  const { locale } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!googleClientId) return;
    const render = () => {
      if (!window.google || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (res: { credential: string }) => {
          signInWithGoogle(res.credential, locale).then(() => onDone?.(), () => setError(true));
        },
      });
      window.google.accounts.id.renderButton(ref.current, { theme: "outline", size: "large", shape: "pill", text: "continue_with", locale });
    };
    if (window.google) { render(); return; }
    const s = document.createElement("script");
    s.src = `https://accounts.google.com/gsi/client?hl=${locale}`;
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, [googleClientId, locale, signInWithGoogle, onDone]);

  if (!googleClientId) return null;
  return (
    <div>
      <div ref={ref} />
      {error && <p role="alert" className="mt-2 text-sm text-accent">Google sign-in failed. Please try again.</p>}
    </div>
  );
}
